import { useEffect, useState } from "react";
import api from "../lib/api";
import {
  UserPlus, Ban, ShieldOff, Archive, ArchiveRestore, Trash2,
  Loader2, Activity as ActivityIcon,
} from "lucide-react";

const EVENT_META = {
  created:     { label: "Created",     icon: UserPlus,       color: "text-emerald-600", bg: "bg-emerald-50" },
  banned:      { label: "Banned",      icon: Ban,            color: "text-rose-700",    bg: "bg-rose-100" },
  unbanned:    { label: "Unbanned",    icon: ShieldOff,      color: "text-emerald-600", bg: "bg-emerald-50" },
  deactivated: { label: "Archived",    icon: Archive,        color: "text-slate-600",   bg: "bg-slate-100" },
  reactivated: { label: "Restored",    icon: ArchiveRestore, color: "text-emerald-600", bg: "bg-emerald-50" },
  deleted:     { label: "Deleted",     icon: Trash2,         color: "text-rose-700",    bg: "bg-rose-100" },
};

const fmtAt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString([], {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
};

/**
 * Activity / moderation history tab for a client profile.
 * Mirrors AppointmentAuditLog. Re-fetches when refreshKey changes so the
 * page can prompt a reload after a ban/archive/restore action.
 */
export default function ClientActivityLog({ clientId, refreshKey = 0 }) {
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(`/clients/${clientId}/activity`)
      .then(r => { if (!cancelled) setEvents(r.data?.events || []); })
      .catch(err => {
        if (!cancelled) {
          if (err.response?.status === 403) setError("You don't have access to view this activity log.");
          else if (err.response?.status === 404) setError("Activity log not found.");
          else setError("Could not load activity log.");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId, refreshKey]);

  if (loading) {
    return (
      <div data-testid="client-activity-loading" className="flex items-center justify-center py-8 text-slate-400">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="client-activity-error" className="text-center py-8 text-sm text-slate-500">{error}</div>
    );
  }

  if (events.length === 0) {
    return (
      <div data-testid="client-activity-empty" className="text-center py-8">
        <ActivityIcon className="w-8 h-8 text-slate-200 mx-auto mb-2" strokeWidth={1.5} />
        <p className="text-sm text-slate-400 font-['Manrope']">No moderation activity yet.</p>
        <p className="text-xs text-slate-300 font-['Manrope'] mt-0.5">Bans, archives, and deletes will appear here.</p>
      </div>
    );
  }

  return (
    <div data-testid="client-activity-list" className="relative">
      <div className="absolute left-4 top-2 bottom-2 w-px bg-slate-200" aria-hidden="true" />
      <ol className="space-y-3">
        {events.map((e) => {
          const meta = EVENT_META[e.event_type] || { label: e.event_type, icon: ActivityIcon, color: "text-slate-500", bg: "bg-slate-100" };
          const Icon = meta.icon;
          const md = e.metadata || {};
          return (
            <li
              key={e.event_id}
              data-testid={`client-activity-${e.event_type}-${e.event_id}`}
              className="relative pl-10">
              <div className={`absolute left-0 top-1 w-8 h-8 rounded-full ${meta.bg} ring-4 ring-white flex items-center justify-center`}>
                <Icon className={`w-4 h-4 ${meta.color}`} strokeWidth={2} />
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-3.5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <p className={`text-sm font-semibold font-['Manrope'] ${meta.color}`}>{meta.label}</p>
                  <p className="text-[11px] text-slate-400 font-['Manrope'] tabular-nums">{fmtAt(e.created_at)}</p>
                </div>
                <p className="text-xs text-slate-500 font-['Manrope'] mt-1">
                  by <span className="font-semibold text-slate-700">{e.actor_name || "—"}</span>
                  {e.actor_role && <span className="text-slate-400"> · {e.actor_role}</span>}
                </p>
                {md.reason && (
                  <p className="text-[11px] text-slate-600 mt-1.5 font-['Manrope']">
                    <span className="text-slate-400">Reason:</span>{" "}
                    <span className="font-medium text-slate-700">{md.reason}</span>
                  </p>
                )}
                {e.event_type === "deleted" && md.cascaded && Object.keys(md.cascaded).length > 0 && (
                  <p className="text-[11px] text-slate-500 mt-1.5 font-['Manrope']">
                    Cascaded:{" "}
                    {Object.entries(md.cascaded).map(([k, v]) => (
                      <span key={k} className="mr-2">
                        <span className="font-semibold text-slate-700">{v}</span>{" "}
                        <span className="text-slate-400">{k}</span>
                      </span>
                    ))}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
